import React from "react";
import { BrowserRouter, Routes, Route, NavLink } from "react-router-dom";
import { Home, BookOpen, PenLine, Heart, Target } from "lucide-react";
import HomePage from "@/pages/HomePage";
import ReaderPage from "@/pages/ReaderPage";
import JournalPage from "@/pages/JournalPage";
import NotesPage from "@/pages/NotesPage";
import PrayerPage from "@/pages/PrayerPage";
import PersonThreadPage from "@/pages/PersonThreadPage";
import EntryComposer from "@/pages/EntryComposer";
import EntryViewer from "@/pages/EntryViewer";
import HighlightsPage from "@/pages/HighlightsPage";
import SavedPage from "@/pages/SavedPage";
import GoalsPage from "@/pages/GoalsPage";
import BottomNav, { DesktopSideNav } from "@/components/BottomNav";
import useMediaQuery from "@/hooks/useMediaQuery";

function Shell({ children }) {
    const isDesktop = useMediaQuery("(min-width: 1024px)");
    const [toast, setToast] = React.useState("");
    React.useEffect(() => {
        const t = sessionStorage.getItem("pending-toast");
        if (t) {
            sessionStorage.removeItem("pending-toast");
            setToast(t);
            const timer = setTimeout(() => setToast(""), 2200);
            return () => clearTimeout(timer);
        }
    }, [children]);
    return (
        <div
            data-testid="app-shell"
            className={
                "bg-sage-paper text-ink " +
                (isDesktop
                    ? "lg:grid lg:grid-cols-[240px_1fr] xl:grid-cols-[280px_1fr] lg:h-screen lg:overflow-hidden"
                    : "min-h-screen max-w-md md:max-w-none mx-auto md:mx-0 relative overflow-x-hidden shadow-2xl md:shadow-none ring-1 md:ring-0 ring-ink-10")
            }
        >
            {isDesktop && (
                <div className="hidden lg:flex flex-col h-screen sticky top-0 border-r border-ink-10 bg-sage-paper/60">
                    <DesktopSideNav />
                    <div className="flex-1 overflow-y-auto">{/* sidebar content depends on route */}</div>
                </div>
            )}
            <div className={isDesktop ? "min-h-screen overflow-y-auto" : ""}>
                {children}
            </div>
            {!isDesktop && <BottomNav />}
            {toast && (
                <div data-testid="toast" className="fixed bottom-24 left-1/2 -translate-x-1/2 z-50 px-4 py-2 rounded-full bg-ink text-white font-ui text-[12px] shadow-lg animate-fade-in">
                    {toast}
                </div>
            )}
        </div>
    );
}

function App() {
    return (
        <BrowserRouter>
            <Routes>
                {/* Home is the new landing page */}
                <Route path="/" element={<Shell><HomePage /></Shell>} />

                {/* Reader has its own shell (uses ChapterSidebar); moved to /read */}
                <Route path="/read" element={<ReaderShell />} />

                {/* Journal / Notes share Shell with BottomNav */}
                <Route path="/journal" element={<Shell><JournalPage /></Shell>} />
                <Route path="/journal/new" element={<Shell><EntryComposer space="journal" /></Shell>} />
                <Route path="/journal/:id" element={<Shell><EntryViewer space="journal" /></Shell>} />
                <Route path="/journal/:id/edit" element={<Shell><EntryComposer space="journal" /></Shell>} />

                <Route path="/notes" element={<Shell><NotesPage /></Shell>} />
                <Route path="/notes/new" element={<Shell><EntryComposer space="notes" /></Shell>} />
                <Route path="/notes/:id" element={<Shell><EntryViewer space="notes" /></Shell>} />
                <Route path="/notes/:id/edit" element={<Shell><EntryComposer space="notes" /></Shell>} />

                <Route path="/prayer" element={<Shell><PrayerPage /></Shell>} />
                <Route path="/prayer/:id" element={<Shell><PersonThreadPage /></Shell>} />

                <Route path="/highlights" element={<Shell><HighlightsPage /></Shell>} />
                <Route path="/bookmarks" element={<Shell><SavedPage /></Shell>} />
                <Route path="/saved" element={<Shell><SavedPage /></Shell>} />
                <Route path="/goals" element={<Shell><GoalsPage /></Shell>} />
                <Route path="/goals/:id" element={<Shell><GoalsPage /></Shell>} />
                <Route path="/goals/:id/edit" element={<Shell><GoalsPage /></Shell>} />
            </Routes>
        </BrowserRouter>
    );
}

function ReaderShell() {
    const isDesktop = useMediaQuery("(min-width: 1024px)");
    return (
        <div
            data-testid="app-shell"
            className={
                "bg-sage-paper text-ink " +
                (isDesktop
                    ? "lg:grid lg:grid-cols-[64px_220px_1fr] xl:grid-cols-[64px_280px_1fr] lg:h-screen lg:overflow-hidden"
                    : "min-h-screen max-w-md md:max-w-none mx-auto md:mx-0 relative overflow-x-hidden shadow-2xl md:shadow-none ring-1 md:ring-0 ring-ink-10")
            }
        >
            {isDesktop && (
                <div className="hidden lg:flex flex-col h-screen sticky top-0 border-r border-ink-10 bg-sage-paper/80 items-center py-3 gap-1">
                    <DesktopSideNavCompact />
                </div>
            )}
            <ReaderPage />
            {!isDesktop && <BottomNav />}
        </div>
    );
}

function DesktopSideNavCompact() {
    const items = [
        { to: "/", label: "Home", icon: Home, testid: "side-nav-home", exact: true },
        { to: "/read", label: "Bible", icon: BookOpen, testid: "side-nav-bible" },
        { to: "/journal", label: "Journal", icon: PenLine, testid: "side-nav-journal" },
        { to: "/prayer", label: "Pray", icon: Heart, testid: "side-nav-prayer" },
        { to: "/goals", label: "Goals", icon: Target, testid: "side-nav-goals" },
    ];
    return items.map(({ to, label, icon: Icon, testid, exact }) => (
        <NavLink
            key={to}
            to={to}
            end={exact}
            data-testid={testid}
            title={label}
            className={({ isActive }) =>
                "w-11 h-11 rounded-xl flex items-center justify-center transition " +
                (isActive ? "bg-moss text-white" : "text-ink-70 hover:bg-moss/10 hover:text-moss-deep")
            }
        >
            <Icon size={18} />
        </NavLink>
    ));
}

export default App;
